import { Suspense } from "react";
import { useLocation } from "react-router-dom";
import AppLoadingScreen from "@/components/AppLoadingScreen";
import SideBar from "@/components/SideBar";
import { WorkbenchLoadingScreen } from "@/components/BatchWorkbench/components/WorkbenchLoadingScreen";
import {
  DatasetStudioFullPageSkeleton,
  datasetStudioSkeletonVariant,
  isDatasetStudioPath,
} from "@/components/DatasetMarketplace/DatasetStudioPageSkeleton";

interface LayoutProps {
  children: React.ReactNode;
  /** Opt-in for features that own their navigation/session reconciliation. */
  contentKey?: string;
}

const Layout = ({ children, contentKey }: LayoutProps) => {
  const location = useLocation();
  const defaultCollapsed = true;
  const datasetStudioRoute = isDatasetStudioPath(location.pathname);
  const workbenchRoute = /^\/(?:live-demo\/)?workbench\//.test(location.pathname);

  const appShell = (
    <SideBar defaultCollapsed={defaultCollapsed} content={children} />
  );

  if (datasetStudioRoute) {
    // Overview shares the browsing session; different datasets still remount.
    const studioSessionKey = contentKey ?? location.pathname.replace(/\/distribution-new\/?$/, "");
    return (
      <div className={datasetStudioSkeletonVariant(location.pathname) === "detail"
        ? "flex h-dvh min-h-0 flex-col overflow-hidden bg-[#FCFAF4] lg:h-auto lg:min-h-screen lg:overflow-visible"
        : "flex min-h-dvh flex-col"}>
        <main className="min-h-0 flex-1">
          <Suspense
            key={studioSessionKey}
            fallback={
              <DatasetStudioFullPageSkeleton
                variant={datasetStudioSkeletonVariant(location.pathname)}
                curatedCatalog={location.pathname.startsWith("/live-demo")}
              />
            }
          >
            {appShell}
          </Suspense>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-1">
        <SideBar
          defaultCollapsed={defaultCollapsed}
          content={
            <Suspense key={contentKey ?? location.pathname} fallback={workbenchRoute ? <WorkbenchLoadingScreen /> : <AppLoadingScreen showMark={false} />}>
              {children}
            </Suspense>
          }
        />
      </main>
    </div>
  );
};

export default Layout;
